/**
 * Email OTP Authentication Hooks
 *
 * These hooks provide high-level email OTP (one-time password) authentication actions
 * that convert Stytch responses to our domain types.
 */
import { useCallback } from 'react';
import { useSendDiscoveryEmailOTP, useAuthenticateDiscoveryEmailOTP } from './stytch-hooks';
// ============================================================================
// Hooks
// ============================================================================
/**
 * Hook to send email OTP for discovery flow
 */
export function useSendEmailOTP() {
    const sendDiscoveryEmailOTP = useSendDiscoveryEmailOTP();
    return useCallback(async (email, options) => {
        const result = await sendDiscoveryEmailOTP(email, {
            login_template_id: 'otp',
            ...options,
        });
        if (result.success) {
            return {
                success: true,
                emailId: result.data?.email_id,
                data: result.data,
            };
        }
        else {
            return {
                success: false,
                error: result.error,
                data: result.data,
            };
        }
    }, [sendDiscoveryEmailOTP]);
}
/**
 * Hook to authenticate email OTP code for discovery flow
 */
export function useAuthenticateEmailOTP() {
    const authenticateDiscoveryEmailOTP = useAuthenticateDiscoveryEmailOTP();
    return useCallback(async (email, code) => {
        const result = await authenticateDiscoveryEmailOTP(email, code);
        if (result.success) {
            const discoveredOrgs = result.discovered_organizations;
            const hasExistingOrganization = Boolean(discoveredOrgs && discoveredOrgs.length > 0);
            return {
                success: true,
                intermediateSessionToken: result.intermediate_session_token,
                emailAddress: result.email_address,
                discoveredOrganizations: discoveredOrgs?.map((org) => ({
                    id: org.organization.organization_id,
                    name: org.organization.organization_name,
                    domain: org.organization.organization_slug || '',
                    member_count: 0,
                    created_at: org.organization.created_at || new Date().toISOString(),
                    updated_at: org.organization.updated_at || new Date().toISOString(),
                    stytch_organization_id: org.organization.organization_id,
                    membershipType: org.membership?.type,
                })),
                hasExistingOrganization,
                data: result.data,
            };
        }
        else {
            return {
                success: false,
                error: result.error,
                data: result.data,
            };
        }
    }, [authenticateDiscoveryEmailOTP]);
}
